/**
 * Ten minutes without activity ends the session (ADR-0046).
 *
 * The server enforces the limit on its own: it refuses a token whose session
 * saw no person-driven request for ten minutes. This provider is the visible
 * half, and keeps the two clocks in step:
 *
 * - **Activity** is a key press, a click, a touch, a scroll — what a person
 *   does. Polling is not: background requests carry `X-Daquma-Background` and
 *   do not count on the server either (`apiFetch({ background: true })`).
 * - **Activity counts the person, not the tab** (ADR-0075). Every tab of one
 *   person shares one server-side session, so a tab that saw activity tells
 *   the others (`tabs.ts`, at most every few seconds) and each tab's clock
 *   takes the latest activity of any tab. An idle background tab therefore
 *   never ends the session someone is using in another tab. The heartbeat
 *   below still comes only from a tab that saw activity itself, so the server
 *   is not asked once per tab.
 * - **Heartbeat.** Reading a long page makes no request, so while the person
 *   is active the provider sends one person-driven `GET /api/v1/me` at most
 *   every four minutes; without it the server would time out someone who is
 *   plainly still there.
 * - **Warning at 8 minutes**, with a countdown and "Stay signed in" (which is
 *   activity, and sends a heartbeat at once).
 * - **The clock survives re-renders.** `useAccessToken` hands back a new
 *   function on most renders (msal-react's fresh arrays, see useNotifications);
 *   live values are read through refs so the timer effect depends on sign-in
 *   alone, and a re-render — the warning opening — never resets the clock.
 * - **At 10 minutes** the session is ended on the server (reason
 *   `idle_timeout`, recorded as `auth.session_timeout`), MSAL forgets the
 *   account, and the person lands on `/login` with the reason shown. A
 *   session ended in another tab — sign-out, idle, refused renewal, or the
 *   server saying so — sends this tab to `/login` the same way, without a
 *   second logout call.
 * - A tab with no account asks the others who is signed in (`who`); this one
 *   answers with the account's username, the hint its silent sign-in needs.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import type { PropsWithChildren } from "react";
import { Button, Group, Modal, Progress, Stack, Text } from "@mantine/core";
import { useMsal } from "@azure/msal-react";
import { apiFetch } from "@/shared/api/apiFetch";
import { useAccessToken } from "./useAccessToken";
import { endSession, loginPathFor } from "./session";
import { postToTabs, subscribeToTabs } from "./tabs";

export const IDLE_LIMIT_MS = 10 * 60_000;
export const WARN_AT_MS = 8 * 60_000;
export const HEARTBEAT_EVERY_MS = 4 * 60_000;
/** How often at most a tab tells the others it saw activity. */
export const SHARE_ACTIVITY_EVERY_MS = 5_000;
const TICK_MS = 1_000;
const ACTIVITY_EVENTS = ["keydown", "mousedown", "touchstart", "wheel", "scroll"] as const;

export function IdleTimeoutProvider({ children }: PropsWithChildren) {
  const { instance, accounts } = useMsal();
  const getToken = useAccessToken();
  const signedIn = accounts.length > 0;
  const username = accounts[0]?.username ?? "";
  // The latest activity of any tab (the idle clock) and of this tab alone
  // (the heartbeat and the sharing).
  const lastActivity = useRef(Date.now());
  const lastLocalActivity = useRef(Date.now());
  const lastShared = useRef(0);
  const lastHeartbeat = useRef(Date.now());
  const expiring = useRef(false);
  const [remainingMs, setRemainingMs] = useState<number | null>(null);
  const getTokenRef = useRef(getToken);
  getTokenRef.current = getToken;
  const instanceRef = useRef(instance);
  instanceRef.current = instance;
  const usernameRef = useRef(username);
  usernameRef.current = username;

  const heartbeat = useCallback(async () => {
    lastHeartbeat.current = Date.now();
    try {
      await apiFetch("/api/v1/me", { token: await getTokenRef.current() });
    } catch {
      // A refused heartbeat is handled by the 401 interceptor.
    }
  }, []);

  const noteLocalActivity = useCallback(() => {
    const now = Date.now();
    lastActivity.current = now;
    lastLocalActivity.current = now;
    if (now - lastShared.current >= SHARE_ACTIVITY_EVERY_MS) {
      lastShared.current = now;
      postToTabs({ type: "activity", at: now });
    }
  }, []);

  const stay = useCallback(() => {
    noteLocalActivity();
    setRemainingMs(null);
    void heartbeat();
  }, [heartbeat, noteLocalActivity]);

  useEffect(() => {
    if (!signedIn) return;
    const now = Date.now();
    lastActivity.current = now;
    lastLocalActivity.current = now;
    lastHeartbeat.current = now;
    for (const event of ACTIVITY_EVENTS) {
      window.addEventListener(event, noteLocalActivity, { passive: true, capture: true });
    }
    const unsubscribe = subscribeToTabs((message) => {
      if (message.type === "activity") {
        lastActivity.current = Math.max(lastActivity.current, Date.now());
        setRemainingMs(null);
      } else if (message.type === "who") {
        if (usernameRef.current) postToTabs({ type: "account", username: usernameRef.current });
      } else if (message.type === "ended") {
        if (expiring.current) return;
        expiring.current = true;
        void instanceRef.current.clearCache().finally(() => {
          window.location.assign(loginPathFor(message.reason));
        });
      }
    });
    const timer = setInterval(() => {
      const tick = Date.now();
      const idle = tick - lastActivity.current;
      if (idle >= IDLE_LIMIT_MS) {
        if (expiring.current) return;
        expiring.current = true;
        void endSession(instanceRef.current, "idle_timeout").finally(() => {
          window.location.assign(loginPathFor("idle_timeout"));
        });
        return;
      }
      if (idle >= WARN_AT_MS) {
        setRemainingMs(IDLE_LIMIT_MS - idle);
        return;
      }
      setRemainingMs(null);
      const activeHere = tick - lastLocalActivity.current < TICK_MS * 60;
      if (activeHere && tick - lastHeartbeat.current >= HEARTBEAT_EVERY_MS) {
        void heartbeat();
      }
    }, TICK_MS);
    return () => {
      clearInterval(timer);
      unsubscribe();
      for (const event of ACTIVITY_EVENTS) {
        window.removeEventListener(event, noteLocalActivity, { capture: true });
      }
    };
  }, [signedIn, heartbeat, noteLocalActivity]);

  const seconds = remainingMs === null ? 0 : Math.max(0, Math.ceil(remainingMs / 1000));
  return (
    <>
      {children}
      <Modal
        opened={signedIn && remainingMs !== null}
        onClose={stay}
        title="Are you still there?"
        centered
        withCloseButton={false}
      >
        <Stack gap="sm">
          <Text size="sm">
            For security you will be signed out after 10 minutes without activity. Signing out in{" "}
            <b>
              {Math.floor(seconds / 60)}:{String(seconds % 60).padStart(2, "0")}
            </b>
            .
          </Text>
          <Progress
            value={(seconds / ((IDLE_LIMIT_MS - WARN_AT_MS) / 1000)) * 100}
            aria-label="Time left before sign-out"
          />
          <Group justify="flex-end">
            <Button
              variant="default"
              onClick={() =>
                void endSession(instance, "sign_out").finally(() =>
                  window.location.assign("/login"),
                )
              }
            >
              Sign out now
            </Button>
            <Button onClick={stay}>Stay signed in</Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
